import { z } from "zod"

/**
 * Публичные соц-данные карточки товара: отзывы и вопросы-ответы (Э6).
 *
 * Эти DTO отдаёт бэкенд на публичных ручках карточки товара у покупателя
 * (`/products/:id/reviews`, `/products/:id/questions`) и на ручке ответа
 * продавца. В отличие от аналитики кабинета (`ProductReviews`), здесь нет
 * коммерческих метрик магазина — только то, что видит любой покупатель.
 */

// --- Отзывы -----------------------------------------------------------------

export const ProductReviewSchema = z.object({
  id: z.string(),
  rating: z.number().int().min(1).max(5),
  text: z.string(),
  authorName: z.string(),
  authorAvatarUrl: z.string().optional(),
  /** Купленный вариант (цвет/размер) — из позиции заказа, если известен. */
  variant: z.string().optional(),
  /** Фото к отзыву. Хранилища под них пока нет — всегда пустой список. */
  photoUrls: z.array(z.string()),
  /** Ответ магазина на отзыв (POST /seller/reviews/:id/reply). Пусто — ответа нет. */
  sellerReply: z.string().optional(),
  /** Кто ответил — название магазина (не имя сотрудника). Есть, только если есть ответ. */
  sellerReplyAuthor: z.string().optional(),
  createdAt: z.string(),
  /** Сколько покупателей отметили отзыв сердечком (тот же счётчик, что столбец «Лайки» у продавца). */
  likes: z.number().int().nonnegative().optional(),
  /** Отметил ли его текущий покупатель. Только для вошедшего покупателя. */
  likedByMe: z.boolean().optional(),
})
export type ProductReview = z.infer<typeof ProductReviewSchema>

/** Ответ на PUT /buyer/likes/reviews/:id — состояние лайка после запроса. */
export const ProductReviewLikeSchema = z.object({
  reviewId: z.string(),
  liked: z.boolean(),
  likes: z.number().int().nonnegative(),
})
export type ProductReviewLike = z.infer<typeof ProductReviewLikeSchema>

/** Сводка по отзывам: средняя оценка, всего и распределение по звёздам. */
export const ProductReviewSummarySchema = z.object({
  average: z.number(),
  count: z.number().int().nonnegative(),
  /** Число отзывов на каждую оценку 1..5. */
  distribution: z.object({
    1: z.number().int().nonnegative(),
    2: z.number().int().nonnegative(),
    3: z.number().int().nonnegative(),
    4: z.number().int().nonnegative(),
    5: z.number().int().nonnegative(),
  }),
  /**
   * «54 продано» и «0 возвратов» из шапки экрана отзывов (`3709:37651`).
   *
   * Раньше публичного источника у этих чисел не было: клиент рисовал нули, а
   * рядом стоял комментарий, что брать их неоткуда — продавцовая аналитика
   * покупателю недоступна. Поэтому считаем их здесь, из тех же заказов, что уже
   * решают «куплен ли товар»: продано — сумма количеств в доставленных и
   * завершённых заказах, возвратов — принятые строки возврата по этому товару.
   *
   * Числа публичные и обезличенные: это свойство ТОВАРА, а не чьих-то покупок,
   * и по ним нельзя узнать, кто именно покупал.
   */
  soldCount: z.number().int().nonnegative(),
  returnCount: z.number().int().nonnegative(),
})
export type ProductReviewSummary = z.infer<typeof ProductReviewSummarySchema>

export const ProductReviewListSchema = z.object({
  productId: z.string(),
  summary: ProductReviewSummarySchema,
  items: z.array(ProductReviewSchema),
  /** Куплен ли товар текущим покупателем (есть доставленный/завершённый заказ).
   *  Присутствует только для авторизованного покупателя; для гостя отсутствует. */
  purchased: z.boolean().optional(),
  /** Может ли текущий покупатель оставить отзыв (куплен и ещё не оценён).
   *  Гейтит панель «Оцените товар»; для гостя отсутствует. */
  canReview: z.boolean().optional(),
})
export type ProductReviewList = z.infer<typeof ProductReviewListSchema>

/** Сортировка ленты отзывов. */
export const ProductReviewSortSchema = z.enum(["newest", "rating"])
export type ProductReviewSort = z.infer<typeof ProductReviewSortSchema>

// --- Вопросы к товару -------------------------------------------------------

export const ProductQuestionStatusSchema = z.enum(["open", "answered", "hidden"])
export type ProductQuestionStatus = z.infer<typeof ProductQuestionStatusSchema>

export const ProductQuestionAnswerSchema = z.object({
  body: z.string(),
  authorName: z.string(),
  createdAt: z.string(),
})
export type ProductQuestionAnswer = z.infer<typeof ProductQuestionAnswerSchema>

export const ProductQuestionSchema = z.object({
  id: z.string(),
  productId: z.string(),
  body: z.string(),
  authorName: z.string(),
  status: ProductQuestionStatusSchema,
  /** Ответ магазина, если продавец уже ответил. */
  answer: ProductQuestionAnswerSchema.optional(),
  createdAt: z.string(),
})
export type ProductQuestion = z.infer<typeof ProductQuestionSchema>

export const ProductQuestionListSchema = z.object({
  productId: z.string(),
  items: z.array(ProductQuestionSchema),
})
export type ProductQuestionList = z.infer<typeof ProductQuestionListSchema>

export const AskQuestionRequestSchema = z.object({
  body: z.string().trim().min(1).max(2000),
})
export type AskQuestionRequest = z.infer<typeof AskQuestionRequestSchema>

export const AnswerQuestionRequestSchema = z.object({
  body: z.string().trim().min(1).max(2000),
})
export type AnswerQuestionRequest = z.infer<typeof AnswerQuestionRequestSchema>

// --- Модерация отзывов (fd-admin-05, миграция 530) --------------------------

/**
 * Жалоба продавца на отзыв в консоли площадки. Сотрудник видит отзыв целиком
 * (вместе с анонимным автором - модерации нужно знать, кто писал), жалобу и
 * решает: скрыть отзыв (`accepted`) или оставить (`declined`), с причиной.
 */
export const ReviewComplaintStatusSchema = z.enum(["open", "accepted", "declined"])
export type ReviewComplaintStatus = z.infer<typeof ReviewComplaintStatusSchema>

export const AdminReviewComplaintSchema = z.object({
  id: z.string(),
  status: ReviewComplaintStatusSchema,
  reason: z.string(),
  description: z.string(),
  attachments: z.array(z.string()),
  createdAt: z.string(),
  resolvedAt: z.string().optional(),
  decisionReason: z.string().optional(),
  decidedBy: z.object({ id: z.string(), name: z.string() }).optional(),
  /** Кто из магазина пожаловался. */
  authorName: z.string().optional(),
  shop: z.object({ id: z.string(), name: z.string() }),
  product: z.object({ id: z.string(), name: z.string(), article: z.string(), photo: z.string().optional() }),
  review: z.object({
    id: z.string(),
    rating: z.number().int().min(1).max(5),
    text: z.string(),
    createdAt: z.string(),
    authorName: z.string(),
    anonymous: z.boolean(),
    photos: z.array(z.string()),
    sellerReply: z.string().optional(),
    hidden: z.boolean(),
    hiddenAt: z.string().optional(),
    hiddenReason: z.string().optional(),
  }),
  /** Сколько ещё жалоб на этот же отзыв (кроме этой). */
  otherComplaints: z.number().int().nonnegative(),
})
export type AdminReviewComplaint = z.infer<typeof AdminReviewComplaintSchema>

export const AdminReviewComplaintPageSchema = z.object({
  items: z.array(AdminReviewComplaintSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
  counts: z.object({
    open: z.number().int().nonnegative(),
    accepted: z.number().int().nonnegative(),
    declined: z.number().int().nonnegative(),
  }),
})
export type AdminReviewComplaintPage = z.infer<typeof AdminReviewComplaintPageSchema>

/** Решение по жалобе: «Скрыть отзыв» или «Оставить», причина обязательна. */
export const ReviewComplaintDecisionRequestSchema = z.object({
  action: z.enum(["hide", "keep"]),
  reason: z.string().trim().min(1).max(500),
})
export type ReviewComplaintDecisionRequest = z.infer<typeof ReviewComplaintDecisionRequestSchema>

/** «Вернуть отзыв»: снять скрытие, причина обязательна. */
export const ReviewRestoreRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
})
export type ReviewRestoreRequest = z.infer<typeof ReviewRestoreRequestSchema>
